// frontend/src/pages/NewUIProfile.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../config/axios';
import { Edit, Check, X } from 'lucide-react';

const NewUIProfile = () => {
    const { user, updateUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [transactions, setTransactions] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [selectedAvatar, setSelectedAvatar] = useState(null);
    const [savingAvatar, setSavingAvatar] = useState(false);
    const [profileData, setProfileData] = useState({
        username: '',
        email: '',
        name: ''
    });
    const [showAllTransactions, setShowAllTransactions] = useState(false);

    // Aplicar clase de fuente al montar
    useEffect(() => {
        document.body.classList.add('acumin-scope');
        document.body.style.overflowX = 'hidden';
        return () => {
            document.body.classList.remove('acumin-scope');
            document.body.style.overflowX = '';
        };
    }, []);

    // Fetch user profile and transactions
    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                setLoading(true);
                if (user) {
                    setProfileData({
                        username: user.username,
                        email: user.email,
                        name: user.name || ''
                    });
                    // Inicializar avatar desde user.image (ej: "3.png" → 3)
                    const match = user.image?.match(/^(\d+)\.png$/);
                    if (match) setSelectedAvatar(parseInt(match[1]));
                }

                const response = await axios.get('/user/transactions');
                if (response.data.success) {
                    setTransactions(response.data.transactions);
                }
            } catch (err) {
                setError(err.response?.data?.message || 'Error al cargar datos del perfil');
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, [user]);

    const handleInputChange = (e) => {
        setProfileData({
            ...profileData,
            [e.target.name]: e.target.value
        });
    };

    const handleSaveProfile = async () => {
        try {
            setLoading(true);
            const response = await axios.put('/user/profile', profileData);
            if (response.data.success) {
                updateUser(response.data.user);
                setEditMode(false);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error al actualizar perfil');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAvatar = async () => {
        if (!selectedAvatar) return;
        try {
            setSavingAvatar(true);
            const response = await axios.put('/user/profile', { avatar: selectedAvatar });
            if (response.data.success) {
                updateUser(response.data.user);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error al actualizar personaje');
        } finally {
            setSavingAvatar(false);
        }
    };

    const handleCancelEdit = () => {
        setProfileData({
            username: user.username,
            email: user.email,
            name: user.name || ''
        });
        setEditMode(false);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('es-AR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Aceptada':
                return <span className="text-green-500">✓</span>;
            case 'Rechazada':
                return <span className="text-red-500">✗</span>;
            case 'En proceso':
                return <span className="text-yellow-500">⟳</span>;
            default:
                return null;
        }
    };

    if (loading && !user) {
        return <div className="flex justify-center items-center h-screen bg-[#2284DF]">
            <span className="text-white text-xl">Cargando...</span>
        </div>;
    }

    const visibleTransactions = showAllTransactions ? transactions : transactions.slice(0, 5);
    const avatares = [1, 2, 3, 4, 5, 6];

    return (
        <div className="min-h-screen bg-[#2284DF] overflow-x-hidden">
            {/* Contenedor principal */}
            <div className="max-w-[calc(100%-100px)] mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8">
                {/* Título */}
                <h1 className="text-3xl md:text-4xl font-extrabold text-white uppercase tracking-wider mb-6">
                    Perfil
                </h1>

                {/* Grid de 2 columnas */}
                <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-6">
                    {/* COLUMNA IZQUIERDA */}
                    <div className="space-y-6">
                        {/* Card 1: General */}
                        <div className="bg-white/95 rounded-2xl shadow-lg p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-extrabold text-[#122436] uppercase tracking-wide">
                                    General
                                </h2>
                                {!editMode ? (
                                    <button
                                        onClick={() => setEditMode(true)}
                                        className="flex items-center text-[#2284DF] hover:text-[#2A91F4] font-semibold"
                                    >
                                        <Edit className="w-4 h-4 mr-1" />
                                        Editar
                                    </button>
                                ) : (
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={handleSaveProfile}
                                            className="flex items-center text-green-600 hover:text-green-700 font-semibold"
                                        >
                                            <Check className="w-4 h-4 mr-1" />
                                            Guardar
                                        </button>
                                        <button
                                            onClick={handleCancelEdit}
                                            className="flex items-center text-red-600 hover:text-red-700 font-semibold"
                                        >
                                            <X className="w-4 h-4 mr-1" />
                                            Cancelar
                                        </button>
                                    </div>
                                )}
                            </div>

                            {error && (
                                <div className="mb-4 bg-red-50 border border-red-200 text-red-600 rounded-lg p-3 text-sm">
                                    {error}
                                </div>
                            )}

                            {/* Grid de 3 columnas */}
                            <div className="grid grid-cols-[auto,2fr,1fr] gap-16 items-start">
                                {/* Columna 1: Avatar */}
                                <div className="w-36 h-36 bg-[#122436] rounded-full flex items-center justify-center overflow-hidden shrink-0">
                                    {user.image?.match(/^\d+\.png$/) ? (
                                        <img
                                            src={`/images/avatares/${user.image}`}
                                            alt={user.username}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : user.image ? (
                                        <img
                                            src={user.image}
                                            alt={user.username}
                                            className="w-full h-full object-contain"
                                        />
                                    ) : (
                                        <span className="text-white text-4xl font-bold">
                                            {user.username[0].toUpperCase()}
                                        </span>
                                    )}
                                </div>

                                {/* Columna 2: Usuario + Email */}
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm text-gray-600 font-semibold mb-1">Usuario:</p>
                                        {editMode ? (
                                            <input
                                                type="text"
                                                name="username"
                                                value={profileData.username}
                                                onChange={handleInputChange}
                                                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                            />
                                        ) : (
                                            <p
                                                className="toon text-[#122436] text-3xl"
                                                style={{ WebkitTextStroke: '0.5px #122436' }}
                                            >
                                                {user.username}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <p className="text-sm text-gray-600 font-semibold mb-1">Email:</p>
                                        {editMode ? (
                                            <input
                                                type="email"
                                                name="email"
                                                value={profileData.email}
                                                onChange={handleInputChange}
                                                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                            />
                                        ) : (
                                            <p className="text-gray-700">{user.email}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Columna 3: Saldo + Botones */}
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm text-gray-600 font-semibold mb-2">Saldo:</p>
                                        <p
                                            className="toon text-[#2284DF] text-3xl"
                                            style={{ WebkitTextStroke: '0.5px #2284DF' }}
                                        >
                                            {user.tickets} TICKETS
                                        </p>
                                    </div>

                                    <div className="flex gap-2">
                                        <a
                                            href="/recharge"
                                            className="inline-block text-center px-4 py-2 bg-[#2284DF] text-white font-bold rounded-lg hover:bg-[#2A91F4] transition-colors uppercase text-xs tracking-wide"
                                        >
                                            Recargar
                                        </a>
                                        <button
                                            className="inline-block text-center px-4 py-2 bg-[#fce503] text-[#122436] font-bold rounded-lg hover:bg-[#fce503]/90 transition-colors uppercase text-xs tracking-wide"
                                        >
                                            Retirar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card 2: Historial de transacciones */}
                        <div className="bg-white/95 rounded-2xl shadow-lg p-6">
                            <h2 className="text-xl font-extrabold text-[#122436] uppercase tracking-wide mb-6">
                                Historial de transacciones
                            </h2>

                            {transactions.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No tienes transacciones registradas.</p>
                            ) : (
                                <>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full">
                                            <thead className="bg-gray-200">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                                                    Fecha
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                                                    Transacción
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                                                    Monto
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                                                    Estado
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {visibleTransactions.map((transaction, idx) => (
                                                <tr key={transaction.id} className="border-b border-black">
                                                    <td className="px-4 py-4 text-sm text-gray-600">
                                                        {formatDate(transaction.date)}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-gray-900">
                                                        <div>
                                                            {transaction.type}
                                                            {transaction.description && (
                                                                <p className="text-xs text-gray-500">{transaction.description}</p>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4 text-sm font-bold">
                                                            <span className={transaction.isNegative ? 'text-red-600' : 'text-green-600'}>
                                                                {transaction.isNegative ? '-' : '+'} ${transaction.amount.toLocaleString()}
                                                            </span>
                                                    </td>
                                                    <td className="px-4 py-4 text-sm">
                                                        <div className="flex items-center gap-1">
                                                            {getStatusIcon(transaction.status)}
                                                            <span className={`font-semibold ${
                                                                transaction.status === 'Aceptada' ? 'text-green-500' :
                                                                    transaction.status === 'Rechazada' ? 'text-red-500' :
                                                                        'text-yellow-500'
                                                            }`}>
                                                                    {transaction.status}
                                                                </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {transactions.length > 5 && (
                                        <div className="mt-4 text-center">
                                            <button
                                                onClick={() => setShowAllTransactions(!showAllTransactions)}
                                                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-semibold text-sm"
                                            >
                                                {showAllTransactions ? 'Ver menos' : 'Ver más transacciones'}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Card 3: Selector de avatares */}
                        <div className="bg-[#fce503] rounded-2xl shadow-lg p-6">
                            <h2 className="text-xl font-extrabold text-[#122436] uppercase tracking-wide mb-4">
                                Elegí tu personaje
                            </h2>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {avatares.map((num) => (
                                    <button
                                        key={num}
                                        onClick={() => setSelectedAvatar(num)}
                                        className={`
                                            relative rounded-xl overflow-hidden
                                            bg-[#122436] transition-all duration-200
                                            aspect-[2/1]
                                            ${selectedAvatar === num
                                            ? 'ring-4 ring-white scale-105'
                                            : 'ring-2 ring-[#122436] hover:ring-white hover:scale-102'
                                        }
                                        `}
                                    >
                                        <img
                                            src={`/images/avatares/${num}.png`}
                                            alt={`Avatar ${num}`}
                                            className="w-full h-full object-cover object-center"
                                        />
                                        {selectedAvatar === num && (
                                            <div className="absolute top-2 right-2 bg-white rounded-full w-6 h-6 flex items-center justify-center">
                                                <Check className="w-4 h-4 text-[#122436]" />
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={handleSaveAvatar}
                                disabled={savingAvatar || !selectedAvatar}
                                className="mt-4 w-full px-6 py-2.5 bg-[#122436] text-white font-extrabold rounded-xl hover:bg-[#1a3654] transition-colors uppercase tracking-wide text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {savingAvatar ? 'Guardando...' : 'Seleccionar'}
                            </button>
                        </div>
                    </div>

                    {/* COLUMNA DERECHA */}
                    <div className="space-y-6">
                        {/* Card 4: Ranking (vacía por ahora) */}
                        <div className="bg-white/95 rounded-2xl shadow-lg p-6 min-h-[300px]">
                            <h2 className="text-xl font-extrabold text-[#122436] uppercase tracking-wide mb-6">
                                Ranking
                            </h2>
                            <div className="flex items-center justify-center h-48 text-gray-400">
                                Próximamente
                            </div>
                        </div>

                        {/* Card 5: Nacional (vacía por ahora) */}
                        <div className="bg-white/95 rounded-2xl shadow-lg p-6 min-h-[300px]">
                            <h2 className="text-xl font-extrabold text-[#122436] uppercase tracking-wide mb-6">
                                Ranking Nacional
                            </h2>
                            <div className="flex items-center justify-center h-48 text-gray-400">
                                Próximamente
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewUIProfile;